import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  IconButton,
  InputAdornment
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Login.scss';

const Login = () => {
  const navigate = useNavigate();
  const { login, isLoading, isAuthenticated } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated() && !isLoading) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to sign in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  return (
    <Box className="login-page">
      {/* Logo at top left */}
      <Box className="login-logo">
        <Box className="logo-icon">♠</Box>
        <Typography variant="h6" className="logo-text">
          Home Game
        </Typography>
      </Box>

      {/* Centered login form */}
      <Box className="login-container">
        <form onSubmit={handleSubmit} className="login-form">
          <Typography variant="h4" className="login-title">
            Sign in
          </Typography>

          <Typography variant="body1" className="signup-prompt">
            Don't have an account?{' '}
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleSignUp();
              }}
              className="signup-link"
            >
              Sign up
            </Link>
          </Typography>

          {error && (
            <Alert severity="error" className="error-alert" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="login-field"
            placeholder="Email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon className="field-icon" />
                </InputAdornment>
              ),
            }}
            disabled={isSubmitting || isLoading}
            autoComplete="email"
          />

          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="login-field"
            placeholder="Password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon className="field-icon" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    className="password-toggle"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            disabled={isSubmitting || isLoading}
            autoComplete="current-password"
          />

          <Box className="login-actions">
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                // TODO: Implement forgot password
                alert('Forgot password feature coming soon!');
              }}
              className="forgot-password-link"
            >
              Forgot password?
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            className="login-button"
            disabled={isSubmitting || isLoading || !email || !password}
            size="large"
          >
            {isSubmitting ? 'Signing in...' : 'Continue'}
          </Button>
        </form>
      </Box>
    </Box>
  );
};

export default Login;

